import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ShiftCode from "@/components/shift/ShiftCode";
import { cn } from "@/utils/cn";
import type { ShiftType } from "@utils/grade-util";
import { Pen, Plus, Trash2Icon } from "lucide-react";
import BtnCancel from "@/components/ui/button/btn-cancle";
import BtnSuccess from "@/components/ui/button/btn-success";

export type ShiftRequirementColumn = {
  id: string;
  label: string;
  count: number;
  isInvalid?: boolean;
  locked?: boolean;
  lockedLabel?: string;
  canRename?: boolean;
  canDelete?: boolean;
};

export type ShiftRequirementRow = {
  constraintKey: ShiftType;
  displayId: string;
  color: string;
};

type ShiftRequirementDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  configuredLabel: string;
  addButtonLabel: string;
  columns: ShiftRequirementColumn[];
  rows: ShiftRequirementRow[];
  totalStaffCount: number;
  configuredTotal: number;
  unassignedCount: number;
  getValue: (columnId: string, shift: ShiftType) => number;
  onValueChange: (columnId: string, shift: ShiftType, value: string) => void;
  onRename?: (columnId: string, currentLabel: string) => void;
  onDelete?: (columnId: string) => void;
  onAdd?: () => void;
  addDisabled?: boolean;
};

export function ShiftRequirementDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  configuredLabel,
  addButtonLabel,
  columns,
  rows,
  totalStaffCount,
  configuredTotal,
  unassignedCount,
  getValue,
  onValueChange,
  onRename,
  onDelete,
  onAdd,
  addDisabled = false,
}: ShiftRequirementDialogProps) {
  const [activeShift, setActiveShift] = useState<ShiftType | null>(
    rows[0]?.constraintKey ?? null
  );

  useEffect(() => {
    if (rows.length === 0) {
      setActiveShift(null);
      return;
    }
    setActiveShift((prev) =>
      prev && rows.some((row) => row.constraintKey === prev)
        ? prev
        : rows[0].constraintKey
    );
  }, [rows]);

  const activeRow = useMemo(
    () =>
      rows.find((row) => row.constraintKey === activeShift) ?? rows[0] ?? null,
    [activeShift, rows]
  );

  const activeConfiguredTotal = useMemo(() => {
    if (!activeRow) return 0;
    return columns.reduce(
      (sum, column) =>
        sum + (Number(getValue(column.id, activeRow.constraintKey)) || 0),
      0
    );
  }, [activeRow, columns, getValue]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[84vh] max-w-[min(680px,92vw)] gap-0 overflow-hidden p-0 font-pretendard">
        <DialogHeader className="border-b border-border bg-background px-4 py-4">
          <div className="min-w-0 pr-7">
            <DialogTitle className="text-lg font-bold tracking-normal text-foreground">
              {title}
            </DialogTitle>
            <DialogDescription className="mt-1.5 text-sm leading-5 text-muted-foreground">
              {description}
            </DialogDescription>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs">
            <span className="font-medium text-foreground">
              전체 {totalStaffCount}명
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="font-medium text-foreground">
              현재 Shift 설정 {activeConfiguredTotal}명
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="font-medium text-foreground">
              {configuredLabel} {configuredTotal}명
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="font-medium text-foreground">
              미지정 {unassignedCount}명
            </span>
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col bg-[#F8FAFC] px-4 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {rows.map((row) => {
              const active = activeRow?.constraintKey === row.constraintKey;
              return (
                <button
                  key={row.constraintKey}
                  type="button"
                  className={cn(
                    "flex h-10 items-center gap-2 rounded-md border bg-background px-3 text-sm font-semibold transition-colors",
                    active
                      ? "border-sig text-sig shadow-sm"
                      : "border-border text-muted-foreground hover:border-sig/40 hover:text-foreground"
                  )}
                  onClick={() => setActiveShift(row.constraintKey)}
                >
                  <ShiftCode code={row.displayId} color={row.color} />
                  <span>{row.displayId}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-3 rounded-md border border-border bg-background shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-3">
              <div className="flex items-center gap-2">
                {activeRow ? (
                  <ShiftCode
                    code={activeRow.displayId}
                    color={activeRow.color}
                  />
                ) : null}
                <div>
                  <div className="text-sm font-bold text-foreground">
                    {activeRow?.displayId ?? "-"} 최소 인원 설정
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    선택한 Shift에 필요한 최소 인원을 대상별로 설정합니다.
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="text-sm font-semibold text-sig">
                  {activeConfiguredTotal}명
                </div>
                {onAdd ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5"
                    disabled={addDisabled}
                    onClick={() => onAdd()}
                  >
                    <Plus className="size-3.5" />
                    {addButtonLabel}
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="max-h-[40vh] overflow-y-auto p-3">
              <div className="grid gap-2">
                {columns.map((column) => {
                  const disabled = Boolean(column.locked);
                  const hasActions = Boolean(column.canRename || column.canDelete);
                  const value =
                    activeRow != null
                      ? getValue(column.id, activeRow.constraintKey)
                      : 0;

                  return (
                    <div
                      key={column.id}
                      className={cn(
                        "group/requirement flex min-h-[62px] items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2.5",
                        disabled && "bg-muted/20"
                      )}
                    >
                      <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          <div
                            className="truncate text-sm font-bold text-foreground"
                            title={column.label}
                          >
                            {column.label}
                          </div>
                        </div>
                        <div className="mt-1 text-xs font-medium text-muted-foreground">
                          배정 인원 {column.count}명
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center gap-2">
                        <div className="flex items-center gap-1.5">
                          {disabled ? (
                            <div
                              className="flex h-8 w-10 items-center justify-center text-sm font-semibold text-muted-foreground"
                              aria-label={`${column.label} ${activeRow?.displayId ?? ""} 최소 인원 ${column.lockedLabel ?? `${value}명 고정`}`}
                            >
                              {value}
                            </div>
                          ) : (
                            <Input
                              value={value}
                              max={99}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              aria-label={`${column.label} ${activeRow?.displayId ?? ""} 최소 인원`}
                              onChange={(event) => {
                                if (!activeRow) return;
                                const nextValue = event.target.value.replace(
                                  /[^0-9]/g,
                                  ""
                                );
                                onValueChange(
                                  column.id,
                                  activeRow.constraintKey,
                                  nextValue
                                );
                              }}
                              className="h-8 w-12 text-center font-semibold"
                            />
                          )}
                          <span className="text-xs font-medium text-muted-foreground">
                            명
                          </span>
                        </div>
                        {hasActions ? (
                          <div className="ml-1 flex w-[52px] justify-end gap-0.5 opacity-40 transition-opacity group-hover/requirement:opacity-100 focus-within:opacity-100">
                            {column.canRename ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                className="size-7 rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                                aria-label={`${column.label} 이름 수정`}
                                onClick={() =>
                                  onRename?.(column.id, column.label)
                                }
                              >
                                <Pen className="size-3.5" />
                              </Button>
                            ) : null}
                            {column.canDelete ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                className="size-7 rounded-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                aria-label={`${column.label} 삭제`}
                                onClick={() => onDelete?.(column.id)}
                              >
                                <Trash2Icon className="size-3.5" />
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="!justify-center border-t border-border bg-background px-4 py-4 sm:justify-center">
          <div className="flex justify-center gap-2">
            <BtnCancel type="button" onClick={onClose}>
              닫기
            </BtnCancel>
            <BtnSuccess type="button" onClick={onConfirm}>
              저장
            </BtnSuccess>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
